// Moments Service
//
// A Moment is a 15-second video posted from the plus icon on a profile. They
// are browsed in the social feed's Moments tab, and leave that feed 72 hours
// after posting — the row and the stored video are both kept, so expiry only
// affects visibility.
//
// The tab previously rendered a hardcoded list of 30 invented users playing
// Google's sample clips. Everything here comes from /feed/moments instead,
// which ranks whole reels (see get_moments_feed, migration 042).

import { Story } from '../../components/Profile/types';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { logger } from '../../utils/logger';
import { uploadToStorage } from '../Profile/mediaUpload';

const API_BASE = `${config.api.baseUrl}/feed`;

function headers(json = true): HeadersInit {
  const h: HeadersInit = { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/**
 * Fetch with one 401/403 recovery attempt.
 *
 * The access token in secureStorage can go stale while supabase-js still holds
 * a valid session, and these calls use `fetch` directly rather than passing
 * through authInterceptor's response hook — so nothing would notice or repair
 * it. performRefresh() treats the Supabase session as the source of truth and
 * re-saves the tokens, so a single retry recovers instead of surfacing an
 * error the user can only fix by signing in again.
 *
 * feedService swallows this same failure by falling back to mock data, which
 * is why a stale token shows up as a broken Moments tab and nowhere else.
 */
async function request(path: string, init: RequestInit = {}, json = false): Promise<any> {
  const send = () => fetch(`${API_BASE}${path}`, { ...init, headers: headers(json) });

  let response = await send();

  // 401 only. The backend returns 401 for a rejected token and reserves 403
  // for "you are not allowed to do this" — the daily Moment cap among them.
  // Refreshing on 403 replayed the request for something a new token could
  // never fix, and reported the plan's limit as an expired session.
  if (response.status === 401) {
    logger.log('[MOMENTS] Token rejected, refreshing session and retrying');
    try {
      await authInterceptor.performRefresh();
      response = await send();
    } catch (error) {
      logger.error('[MOMENTS] Token refresh failed:', error);
    }
  }

  if (!response.ok) {
    // Include the status and the backend's own message: a bare statusText
    // ("") tells you nothing about whether this was auth, a bad route, or a
    // server error.
    let detail = response.statusText;
    let code: string | undefined;
    try {
      const body = await response.json();
      detail = body?.message || body?.error || detail;
      code = typeof body?.error === 'string' ? body.error : undefined;
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(
      response.status === 401
        ? 'Your session expired. Please sign in again.'
        : response.status === 403
          // Already a sentence written for the user — the HTTP status would
          // only add noise.
          ? (detail || 'You are not allowed to do that right now.')
          : `${detail || 'Request failed'} (HTTP ${response.status})`,
    ) as Error & { status?: number; code?: string };
    err.status = response.status;
    err.code = code;
    throw err;
  }

  const body = await response.json();
  return body.data;
}

/** Daily Moment capacity for an account, resolved from its tier. */
export interface MomentAllowance {
  tier: string;
  /** null when the tier has no cap. */
  dailyLimit: number | null;
  unlimited: boolean;
  used: number;
  remaining: number | null;
}

/** One author's Moments, played as a sequence before advancing to the next. */
export interface MomentReel {
  userId: string;
  userName: string;
  avatar: string;
  momentCount: number;
  unwatchedCount: number;
  hasUnwatched: boolean;
  moments: Story[];
}

interface RawMoment {
  id: string;
  userId: string;
  videoUrl: string;
  thumbnailUrl?: string | null;
  durationSeconds?: number | null;
  createdAt: string;
  expiresAt: string;
  views?: number;
  viewedByMe?: boolean;
}

// The Story shape the existing viewer renders. `isActive` stays true because
// the backend only returns Moments inside the 72-hour window; the client-side
// expiry filter is a second line of defence for a tab left open past it.
function toStory(row: RawMoment): Story {
  return {
    id: row.id,
    userId: row.userId,
    videoUrl: row.videoUrl,
    thumbnailUrl: row.thumbnailUrl ?? undefined,
    createdAt: new Date(row.createdAt),
    expiresAt: new Date(row.expiresAt),
    views: row.views ?? 0,
    isActive: true,
    viewedByMe: row.viewedByMe ?? false,
  };
}

export const MAX_MOMENT_DURATION_SECONDS = 15;

class MomentsService {
  /**
   * The Moments tab payload. Reels arrive ordered by the backend — unwatched
   * authors first, then affinity and freshness — so callers must preserve the
   * order rather than re-sorting.
   */
  async getMomentsFeed(limit = 60): Promise<MomentReel[]> {
    const data = await request(`/moments?limit=${limit}`);

    return ((data ?? []) as Array<Omit<MomentReel, 'moments'> & { moments: RawMoment[] }>).map((reel) => ({
      ...reel,
      moments: (reel.moments ?? []).map(toStory),
    }));
  }

  /** A specific person's Moments, for their profile. Includes expired ones. */
  async getUserMoments(userId: string): Promise<Story[]> {
    const data = await request(`/moments/user/${userId}`);
    return ((data ?? []) as RawMoment[]).map(toStory);
  }

  /**
   * Uploads the video (and its captured first frame) to Storage, then records
   * the Moment. Bytes go through the same uploadToStorage path profile media
   * uses, so a Moment lands in the poster's own folder and satisfies the
   * per-user Storage policies.
   */
  async postMoment(userId: string, file: File, durationSeconds: number, thumbnail?: Blob | null): Promise<Story> {
    const extension = file.type === 'video/quicktime' ? 'mov'
      : file.type === 'video/webm' ? 'webm'
      : 'mp4';
    const videoUrl = await uploadToStorage(userId, file, extension, file.type);

    let thumbnailUrl: string | undefined;
    if (thumbnail) {
      try {
        thumbnailUrl = await uploadToStorage(userId, thumbnail, 'jpg', 'image/jpeg');
      } catch (error) {
        // A missing thumbnail degrades to the video's own first frame rather
        // than failing a Moment whose video already uploaded.
        logger.error('[MOMENTS] Thumbnail upload failed:', error);
      }
    }

    const data = await request('/moments', {
      method: 'POST',
      body: JSON.stringify({
        videoUrl,
        thumbnailUrl,
        durationSeconds: Math.round(durationSeconds),
      }),
    }, true);
    return toStory(data);
  }

  /**
   * Marks a Moment watched, which is what moves its author out of the
   * unwatched-first band on the next load. Fire-and-forget: failing to record
   * a view must never interrupt playback.
   */
  markViewed(momentId: string): void {
    request(`/moments/${momentId}/view`, { method: 'POST' })
      .catch((error) => logger.error('[MOMENTS] Failed to record view:', error));
  }

  /**
   * Moments this account may still post today: free 3, Premium 10, Premium
   * Plus unlimited. Resolved server-side from the subscription.
   */
  async getMomentAllowance(): Promise<MomentAllowance | null> {
    try {
      return await request('/moments/allowance');
    } catch (error) {
      logger.error('[MOMENTS] Failed to load allowance:', error);
      return null;
    }
  }

  async deleteMoment(momentId: string): Promise<void> {
    await request(`/moments/${momentId}`, { method: 'DELETE' });
  }
}

export const momentsService = new MomentsService();
